import { useMemo } from 'react';
import { Select, type SelectProps } from 'antd';
import { useTranslation } from 'react-i18next';
import { useAllTenantsWithCompanies } from '@/hooks/useAllTenantsWithCompanies';

export interface TenantSelectProps extends Omit<SelectProps<string>, 'options' | 'loading'> {
  /** Tenant IDs to leave out (e.g. tenants the user already belongs to) */
  excludeIds?: string[];
}

/**
 * Searchable single tenant (client) picker for admin forms. Works as a controlled antd Form field.
 */
export function TenantSelect({ excludeIds = [], placeholder, ...selectProps }: TenantSelectProps) {
  const { t } = useTranslation('admin');
  const { tenants, isLoading } = useAllTenantsWithCompanies();

  const options = useMemo(
    () =>
      tenants
        .filter((tenant) => !excludeIds.includes(tenant.id))
        .map((tenant) => ({ value: tenant.id, label: tenant.name })),
    [tenants, excludeIds]
  );

  return (
    <Select
      showSearch
      optionFilterProp="label"
      loading={isLoading}
      placeholder={placeholder ?? t('tenantPickers.tenantPlaceholder')}
      notFoundContent={isLoading ? undefined : t('tenantPickers.noTenants')}
      {...selectProps}
      options={options}
    />
  );
}
